import React from 'react';
import { useTheme } from '../store/useTheme';
import { RobotIcon } from './RobotIcon';
import { getVirtualRivalName, isVirtualRivalOpponentType, ZENDA_COACH } from '../constants/virtualRivals';
import { clsx } from 'clsx';
import { BookOpen, Lightbulb, RotateCcw, Clock, Zap, Infinity } from 'lucide-react';
import type { ExchangeMode } from '../types/GameState';
import { getTileExchangeRuleSummary } from '../utils/tileExchange';

interface GameSummaryProps {
  level: 1 | 2 | 3;
  language?: string;
  dictionaryMode?: string;
  gameMode?: string;
  boardCategory?: string;
  opponentType?: string;
  botDifficulty?: string;
  opponentName?: string;
  coachMode?: boolean;
  coachId?: string;
  swapMode?: string;
  exchangeMode?: ExchangeMode;
  swapsAllowed?: number;
  timeMode?: string;
  turnTimeSeconds?: number;
  currentTime?: number;
  className?: string;
  showOpponentName?: boolean;
  placedCount?: number;
}

export const GameSummary: React.FC<GameSummaryProps> = ({
  level,
  language = 'es',
  dictionaryMode = 'standard',
  gameMode = 'rapida',
  boardCategory = 'VERBO',
  opponentType = 'virtual',
  botDifficulty = 'nilo',
  opponentName,
  coachMode = false,
  exchangeMode,
  swapsAllowed,
  timeMode = 'unlimited',
  turnTimeSeconds = 180,
  currentTime,
  className = "",
  showOpponentName = true
}) => {
  const { theme } = useTheme();
  const isChat1 = theme === 'chat-1';
  const isPremium = theme === 'premium' || isChat1 || theme === 'playful' || theme === 'clean-compact' || theme === 'clean-blue-contrast' || theme === 'mono-premium';
  const isCompact = false;

  const styles = {
    1: {
      text: "text-[15px] font-medium leading-[22px]",
      robotSize: 18 as const,
      iconSize: 15,
      iconGap: "gap-[6px]",
      blockGap: "mx-[8px]",
      container: "flex-wrap justify-center text-center py-1"
    },
    2: {
      text: "text-[13px] font-medium leading-[18px]",
      robotSize: 16 as const,
      iconSize: 13,
      iconGap: "gap-[5px]",
      blockGap: "mx-[6px]",
      container: "flex-nowrap justify-start text-left overflow-x-auto no-scrollbar py-0"
    },
    3: {
      text: "text-[11px] font-bold leading-[14px]",
      robotSize: 15 as const,
      iconSize: 12,
      iconGap: "gap-[4px]",
      blockGap: "mx-[4px]",
      container: isPremium 
        ? `flex-nowrap justify-start items-center text-left overflow-x-auto no-scrollbar ${isCompact ? 'py-0.5' : 'py-1.5'}` 
        : "flex-nowrap justify-start items-center text-left overflow-x-auto no-scrollbar py-0"
    }
  }[level];

  const s = styles;

  const dictText = dictionaryMode === 'basic' ? 'Básico' : dictionaryMode === 'standard' ? 'Intermedio' : dictionaryMode === 'advanced' ? 'Avanzado' : 'Total';
  const sizeText = gameMode === 'mini' ? 'Mini' : gameMode === 'rapida' ? 'Compacto' : 'Normal';
  const distText = boardCategory === 'VERBO' ? 'Verbo' : boardCategory === 'PERSONAL' ? 'Personal' : 'Social';
  
  const isVirtualRival = isVirtualRivalOpponentType(opponentType);
  const opponent = isVirtualRival
    ? getVirtualRivalName(botDifficulty)
    : opponentName || 'Rival';

  const coach = coachMode ? ZENDA_COACH.displayName : null;
  
  const exchangeRule = getTileExchangeRuleSummary({ exchangeMode, swapsAllowed: swapsAllowed ?? 0 });
  const isSwapAndPlay = exchangeRule.mode === 'exchange_and_continue';
  const isTimed = timeMode !== 'unlimited' && turnTimeSeconds !== null;
  
  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const Separator = () => isPremium ? null : <span className={clsx("opacity-40 shrink-0", s.blockGap)}>·</span>;

  const PremiumChip = ({ children, colorClass }: { children: React.ReactNode, colorClass: string }) => (
    <div className={clsx(
      "flex items-center rounded-full border opacity-90 shrink-0 mr-1.5 transition-all shadow-sm",
      isCompact ? "px-2 py-0.5 gap-1" : "px-3 py-1 gap-1.5",
      isChat1 ? "bg-[#FFFDF9] border-[#E8DCC8] text-[#7B6F61]" : `border-current ${colorClass}`
    )}>
      {children}
    </div>
  );

  const SwapRuleIndicator = ({ allowed }: { allowed: boolean }) => (
    <span className="relative inline-flex items-center justify-center">
      <RotateCcw
        size={s.iconSize}
        strokeWidth={2.5}
        aria-label={exchangeRule.label}
        className={clsx(
          "shrink-0",
          allowed ? "text-mult-tl" : "text-amber-500"
        )}
      />
    </span>
  );

  return (
    <div className={clsx("flex items-center text-text-secondary select-none", s.text, s.container, className)}>
      {/* Dictionary Mode */}
      {isPremium ? (
        <PremiumChip colorClass="text-indigo-600 bg-indigo-50/50 border-indigo-100">
          <BookOpen size={s.iconSize} strokeWidth={2.5} className={clsx("shrink-0", isChat1 && "text-[#0F8B8D]")} />
          <span className="whitespace-nowrap uppercase tracking-wider text-[9px] font-black">{dictText}</span>
        </PremiumChip>
      ) : (
        <div className={clsx("flex items-center shrink-0 text-indigo-500/80", s.iconGap)}>
          <BookOpen size={s.iconSize} strokeWidth={2.5} className="shrink-0" />
          <span className="whitespace-nowrap uppercase tracking-wider text-[10px]">{dictText}</span>
        </div>
      )}

      {/* Robot / Opponent Type Icon */}
      {showOpponentName && (isVirtualRival || level < 3) && (
        <>
          <Separator />
          {isPremium ? (
            <PremiumChip colorClass={isVirtualRival ? "text-mult-dl bg-blue-50/50 border-blue-100" : "text-text-muted bg-gray-50/50 border-gray-100"}>
              <RobotIcon size={s.robotSize} className={clsx("shrink-0", isChat1 && "text-[#C9A86A]")} />
              <span className="whitespace-nowrap uppercase tracking-wider text-[9px] font-black">{opponent}</span>
            </PremiumChip>
          ) : (
            <div className={clsx("flex items-center shrink-0", s.iconGap, isVirtualRival ? "text-mult-dl" : "text-text-muted")}>
              <RobotIcon size={s.robotSize} className="shrink-0" />
              <span className="whitespace-nowrap">{opponent}</span>
            </div>
          )}
        </>
      )}

      {/* Coach Mode */}
      {coachMode && (
        <>
          <Separator />
          {isPremium ? (
            <PremiumChip colorClass="text-mult-tl bg-green-50/50 border-green-100">
              <Lightbulb size={s.iconSize} strokeWidth={2.5} className={clsx("shrink-0", isChat1 && "text-[#0F8B8D]")} />
              <span className="whitespace-nowrap uppercase tracking-wider text-[9px] font-black">{coach || 'ZENDA'}</span>
            </PremiumChip>
          ) : (
            <div className={clsx("flex items-center shrink-0 text-mult-tl", s.iconGap)}>
              <Lightbulb size={s.iconSize} strokeWidth={2.5} className="shrink-0" />
              <span className="whitespace-nowrap uppercase tracking-wider text-[10px]">{coach || 'ZENDA'}</span>
            </div>
          )}
        </>
      )}

      {/* Swaps Allowed or Exchange Icon */}
      <Separator />
      {isPremium ? (
        <PremiumChip colorClass={isSwapAndPlay ? "text-mult-tl bg-green-50/50 border-green-100 shadow-green-100/50" : "text-amber-600 bg-amber-50/50 border-amber-100 shadow-amber-100/50"}>
          <SwapRuleIndicator allowed={isSwapAndPlay} />
        </PremiumChip>
      ) : (
        <div className={clsx("flex items-center shrink-0", s.iconGap, isSwapAndPlay ? "text-mult-tl" : "text-amber-500/90")}>
          <SwapRuleIndicator allowed={isSwapAndPlay} />
        </div>
      )}

      {/* Time Mode / Current Time */}
      <Separator />
      {isPremium ? (
        <PremiumChip colorClass={isTimed ? "text-amber-600 bg-amber-50/50 border-amber-100" : "text-text-muted bg-gray-50/50 border-gray-100"}>
          {isTimed ? (
            <Clock size={s.iconSize} strokeWidth={2.5} className={clsx("shrink-0", isChat1 && "text-[#C9A86A]")} />
          ) : (
            <Infinity size={s.iconSize} strokeWidth={2.5} className={clsx("shrink-0", isChat1 && "text-[#C9A86A]")} />
          )}
        </PremiumChip>
      ) : (
        <div className={clsx("flex items-center shrink-0", s.iconGap, isTimed ? "text-amber-500/80" : "text-text-muted")}>
          {isTimed ? (
            <Clock size={s.iconSize} strokeWidth={2.5} className="shrink-0" />
          ) : (
            <Infinity size={s.iconSize} strokeWidth={2.5} className="shrink-0" />
          )}
        </div>
      )}

      {/* Level / Status - Extra for Level 1 */}
      {level === 1 && (
        <>
          <Separator />
          {isPremium ? (
            <PremiumChip colorClass="text-primary-accent bg-cyan-50/50 border-cyan-100">
              <Zap size={s.iconSize} strokeWidth={2.5} className="shrink-0" />
              <span className="whitespace-nowrap uppercase tracking-wider text-[9px] font-black">{sizeText}</span>
            </PremiumChip>
          ) : (
            <div className={clsx("flex items-center shrink-0 text-primary-accent", s.iconGap)}>
              <Zap size={s.iconSize} strokeWidth={2.5} className="shrink-0" />
              <span className="whitespace-nowrap">{sizeText}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

